import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SEO from '../SEO';
import HelpDeskShell from './HelpDeskShell';
import HubView from './HubView';
import CategoryView from './CategoryView';
import ArticleView from './ArticleView';
import SearchView from './SearchView';
import {
    fetchHelpCategories,
    fetchHelpArticles,
    fetchHelpArticle,
    fetchHelpSearch,
    HelpCategory,
    HelpArticleMeta,
    HelpArticleFull,
    HelpSearchHit,
} from './helpdeskApi';
import './helpdesk.css';

interface HelpDeskProps {
    /** Accepted for prop-signature parity with App.tsx's route wiring (see
     * ../blog/Blog.tsx, which accepts the same optional prop and likewise
     * does not call it — its chrome links back to "/" directly instead). */
    onBack?: () => void;
}

const POPULAR_ARTICLE_LIMIT = 6;
const SITE_ORIGIN = 'https://traffic-creator.com';

interface HelpSchemaInput {
    canonicalPath: string;
    title: string;
    description: string;
    breadcrumbs?: { label: string; href: string }[];
    articleStructuredData?: Record<string, unknown>[];
}

const asAbsoluteUrl = (path: string): string => (
    path.startsWith('http') ? path : `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`
);

const withoutNestedContext = (node: Record<string, unknown>): Record<string, unknown> => {
    const { '@context': _context, ...rest } = node;
    return rest;
};

/**
 * Runtime structured-data contract for every Help Center view. Article schema
 * returned by the publish-gated API is folded into the same graph so client
 * navigation and direct server responses describe the same canonical page.
 */
export const buildHelpDeskSchema = ({
    canonicalPath,
    title,
    description,
    breadcrumbs,
    articleStructuredData = [],
}: HelpSchemaInput): Record<string, unknown> => {
    const canonicalUrl = asAbsoluteUrl(canonicalPath);
    const trail = breadcrumbs?.length
        ? breadcrumbs
        : [{ label: 'Help Center', href: '/helpdesk' }];
    const breadcrumbId = `${canonicalUrl}#breadcrumb`;
    const articleNodes = articleStructuredData.flatMap((entry) => {
        const graph = entry['@graph'];
        if (Array.isArray(graph)) {
            return graph.filter(
                (node): node is Record<string, unknown> => Boolean(node) && typeof node === 'object' && !Array.isArray(node)
            );
        }
        return [entry];
    }).map(withoutNestedContext);

    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: title,
                description,
                isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
                breadcrumb: { '@id': breadcrumbId },
            },
            {
                '@type': 'BreadcrumbList',
                '@id': breadcrumbId,
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: `${SITE_ORIGIN}/`,
                    },
                    ...trail.map((entry, index) => ({
                        '@type': 'ListItem',
                        position: index + 2,
                        name: entry.label,
                        item: asAbsoluteUrl(entry.href),
                    })),
                ],
            },
            ...articleNodes,
        ],
    };
};

/**
 * Help Center orchestrator. Handles four views behind the single
 * `/helpdesk`, `/helpdesk/search`, `/helpdesk/:category`, and
 * `/helpdesk/:category/:slug` routes (see App.tsx), the same way Blog.tsx
 * switches between its index and `:slug` views via useParams. Owns all data
 * fetching; each *View component is presentational.
 */
const HelpDesk: React.FC<HelpDeskProps> = () => {
    const { category: categorySlug, slug: articleSlug } = useParams<{ category?: string; slug?: string }>();
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get('q') || '';
    const isSearchRoute = categorySlug === 'search';

    const [categories, setCategories] = useState<HelpCategory[]>([]);
    const [categoryArticles, setCategoryArticles] = useState<HelpArticleMeta[]>([]);
    const [allArticles, setAllArticles] = useState<HelpArticleMeta[]>([]);
    const [activeArticle, setActiveArticle] = useState<HelpArticleFull | null>(null);
    const [searchResults, setSearchResults] = useState<HelpSearchHit[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSearchLoading, setIsSearchLoading] = useState(false);

    // Categories are needed by every view (hub grid, category related-chips,
    // search zero-results fallback), so fetch once up front.
    useEffect(() => {
        let cancelled = false;
        fetchHelpCategories('en').then((data) => {
            if (!cancelled) setCategories(data);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // Hub view: fetch the full article list once to derive "popular" (first
    // N across all categories, in backend category order — the backend has
    // no dedicated popularity signal yet, see deviations in the final report).
    useEffect(() => {
        if (categorySlug) return; // only needed on the hub
        let cancelled = false;
        setIsLoading(true);
        fetchHelpArticles(undefined, 'en').then((data) => {
            if (cancelled) return;
            setAllArticles(data);
            setIsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [categorySlug]);

    // Category view: fetch articles scoped to the active category.
    useEffect(() => {
        if (!categorySlug || isSearchRoute || articleSlug) return;
        let cancelled = false;
        setIsLoading(true);
        fetchHelpArticles(categorySlug, 'en').then((data) => {
            if (cancelled) return;
            setCategoryArticles(data);
            setIsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [categorySlug, isSearchRoute, articleSlug]);

    // Article view: fetch the full article (content + toc + structuredData).
    useEffect(() => {
        if (!categorySlug || !articleSlug || isSearchRoute) return;
        let cancelled = false;
        setIsLoading(true);
        setActiveArticle(null);
        fetchHelpArticle(categorySlug, articleSlug, 'en').then((data) => {
            if (cancelled) return;
            setActiveArticle(data);
            setIsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [categorySlug, articleSlug, isSearchRoute]);

    // Also load the category's article list on the article route, so the
    // sidebar "related articles" list has data (same backend call as the
    // category view, just also triggered when an articleSlug is present).
    useEffect(() => {
        if (!categorySlug || !articleSlug || isSearchRoute) return;
        let cancelled = false;
        fetchHelpArticles(categorySlug, 'en').then((data) => {
            if (!cancelled) setCategoryArticles(data);
        });
        return () => {
            cancelled = true;
        };
    }, [categorySlug, articleSlug, isSearchRoute]);

    // Search view: fetch results whenever ?q= changes.
    useEffect(() => {
        if (!isSearchRoute) return;
        const term = searchQuery.trim();
        if (term.length < 2) {
            setSearchResults([]);
            setIsSearchLoading(false);
            return;
        }
        let cancelled = false;
        setIsSearchLoading(true);
        fetchHelpSearch(term, 'en', 20).then((data) => {
            if (cancelled) return;
            setSearchResults(data);
            setIsSearchLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [isSearchRoute, searchQuery]);

    const activeCategory = categorySlug && !isSearchRoute ? categories.find((c) => c.slug === categorySlug) : undefined;
    const relatedCategories = activeCategory
        ? activeCategory.related.map((slug) => categories.find((c) => c.slug === slug)).filter((c): c is HelpCategory => Boolean(c))
        : [];
    const relatedArticlesForActive = activeArticle
        ? categoryArticles.filter((a) => a.url !== activeArticle.url).slice(0, 4)
        : [];
    const popularArticles = allArticles.slice(0, POPULAR_ARTICLE_LIMIT);

    // ---- SEO per view ----
    let seoTitle = 'TrafficCreator Help Center';
    let seoDescription = 'Support, tutorials, billing help, GA4 analytics, and campaign delivery guides for TrafficCreator.';
    let breadcrumbs: { label: string; href: string }[] | undefined;

    if (articleSlug && categorySlug && !isSearchRoute && activeArticle) {
        seoTitle = activeArticle.title;
        seoDescription = activeArticle.metaDescription || activeArticle.summary || seoDescription;
        breadcrumbs = [
            { label: 'Help Center', href: '/helpdesk' },
            { label: activeArticle.categoryTitle, href: `/helpdesk/${activeArticle.category}` },
            { label: activeArticle.title, href: activeArticle.url },
        ];
    } else if (isSearchRoute) {
        seoTitle = searchQuery ? `Search: ${searchQuery} — TrafficCreator Help` : 'Search — TrafficCreator Help';
        seoDescription = 'Search the TrafficCreator help center.';
        breadcrumbs = [
            { label: 'Help Center', href: '/helpdesk' },
            { label: 'Search', href: '/helpdesk/search' },
        ];
    } else if (categorySlug && activeCategory) {
        seoTitle = `${activeCategory.title} — TrafficCreator Help Center`;
        seoDescription = activeCategory.short;
        breadcrumbs = [
            { label: 'Help Center', href: '/helpdesk' },
            { label: activeCategory.title, href: `/helpdesk/${activeCategory.slug}` },
        ];
    }

    const canonicalPath = articleSlug && categorySlug && !isSearchRoute
        ? `/helpdesk/${categorySlug}/${articleSlug}`
        : categorySlug && !isSearchRoute
            ? `/helpdesk/${categorySlug}`
            : isSearchRoute
                ? '/helpdesk/search'
                : '/helpdesk';
    const pageSchema = buildHelpDeskSchema({
        canonicalPath,
        title: seoTitle,
        description: seoDescription,
        breadcrumbs,
        articleStructuredData: activeArticle?.structuredData,
    });

    return (
        <HelpDeskShell breadcrumbs={breadcrumbs}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                type={articleSlug ? 'article' : 'website'}
                schema={pageSchema}
                noindex={isSearchRoute}
            />

            {isSearchRoute ? (
                <SearchView
                    query={searchQuery}
                    results={searchResults}
                    categories={categories}
                    isLoading={isSearchLoading}
                />
            ) : articleSlug && categorySlug ? (
                activeArticle ? (
                    <ArticleView
                        article={activeArticle}
                        categoryTitle={activeArticle.categoryTitle}
                        relatedArticles={relatedArticlesForActive}
                    />
                ) : !isLoading ? (
                    <section className="hc-wrap hc-section hc-section--tight">
                        <div className="hc-noresult">
                            <h2>Page not found</h2>
                            <p>That help article doesn&rsquo;t exist here. Head back to the help center.</p>
                            <a className="hc-btn hc-btn--primary" href="/helpdesk">Back to Help Center</a>
                        </div>
                    </section>
                ) : null
            ) : categorySlug ? (
                activeCategory ? (
                    <CategoryView category={activeCategory} articles={categoryArticles} relatedCategories={relatedCategories} />
                ) : !isLoading && categories.length > 0 ? (
                    <section className="hc-wrap hc-section hc-section--tight">
                        <div className="hc-noresult">
                            <h2>Page not found</h2>
                            <p>That help category doesn&rsquo;t exist here. Head back to the help center.</p>
                            <a className="hc-btn hc-btn--primary" href="/helpdesk">Back to Help Center</a>
                        </div>
                    </section>
                ) : null
            ) : (
                <HubView categories={categories} popular={popularArticles} />
            )}
        </HelpDeskShell>
    );
};

export default HelpDesk;
